import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type {
  FeatureControlFrame,
  ControlFrameCharacteristic,
  DimensionType,
  MaterialCondition,
  DatumReference,
} from '@frontend/api';
import {
  DIMENSION_TYPES,
  MATERIAL_CONDITIONS,
  POSITION_SYMBOL,
  RUNES_CALLOUT_TYPES,
} from '@frontend/api';
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  IconButton,
  CloseIcon,
  Input,
  VStack,
  GDTControlFrame,
  Flex,
  HStack,
} from '@frontend/components';
import type {
  GDTCharacteristic,
  GDTModifier,
  ToleranceZoneShape,
  DatumReference as GDTDatumReference,
  GDTControlFrameProps,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import {
  CharacteristicSelect,
  DimensionTypeSelect,
  MaterialConditionSelect,
} from './shared/TypedSelects';
import {
  getFieldError as getFieldErrorUtils,
  getFieldTouched as getFieldTouchedUtils,
} from './shared/formUtils';
import {
  createCharacteristicValidation,
  createDimensionTypeValidation,
  createRequiredStringNumberField,
  createBooleanField,
  createMaterialConditionValidation,
  createUniqueDatumsValidation,
} from './shared/validation';
import { buildPrecisionValueObject } from './shared/valueBuilders';

// Reusable types and components for CompositeControlFrameForm
export type ControlFrameFormValues = {
  characteristic: ControlFrameCharacteristic;
  dimType: DimensionType;
  value: string;
  hasMatCon: boolean;
  matCon?: MaterialCondition;
  datums: DatumFormValue[];
};

type DatumFormValue = {
  datum: string;
  hasMatCon: boolean;
  matCon?: MaterialCondition;
};

export const initialControlFrameValues: ControlFrameFormValues = {
  characteristic: POSITION_SYMBOL,
  dimType: DIMENSION_TYPES.LINEAR,
  value: '0',
  hasMatCon: false,
  matCon: undefined,
  datums: [],
};

const initialDatumValue: DatumFormValue = {
  datum: '',
  hasMatCon: false,
  matCon: undefined,
};

export const controlFrameValidationSchema = object().shape({
  characteristic: createCharacteristicValidation(),
  dimType: createDimensionTypeValidation(),
  value: createRequiredStringNumberField('Value'),
  hasMatCon: createBooleanField(),
  matCon: createMaterialConditionValidation('hasMatCon'),
  datums: createUniqueDatumsValidation(),
});

// Reusable DatumField component for use in CompositeControlFrameForm
type DatumFieldProps = {
  datum: DatumFormValue;
  index: number;
  onUpdate: (index: number, datum: DatumFormValue) => void;
  onRemove: (index: number) => void;
  errors?: unknown;
  touched?: unknown;
};

const DatumField = ({
  datum,
  index,
  onUpdate,
  onRemove,
  errors,
  touched,
}: DatumFieldProps) => {
  return (
    <FormControl>
      <FormLabel>Datum {index + 1}</FormLabel>
      <HStack>
        <Input
          type="text"
          value={datum.datum}
          onChange={(e) =>
            onUpdate(index, { ...datum, datum: e.target.value.toUpperCase() })
          }
          placeholder="-"
          maxLength={1}
          size="sm"
          width="60px"
          textAlign="center"
          fontSize="lg"
        />
        {errors?.datum && touched?.datum && (
          <FormErrorMessage>{errors.datum}</FormErrorMessage>
        )}

        <HStack flexGrow={1} justifyContent="flex-end">
          <Checkbox
            isChecked={datum.hasMatCon}
            onChange={(e) =>
              onUpdate(index, {
                ...datum,
                hasMatCon: e.target.checked,
                matCon: e.target.checked ? MATERIAL_CONDITIONS.MMC : undefined,
              })
            }
            size="sm"
          >
            Mat. Cond.
          </Checkbox>

          {datum.hasMatCon && (
            <Box flexGrow={1}>
              <MaterialConditionSelect
                name={`datum-${index}-matCon`}
                label=""
                value={datum.matCon}
                onChange={(value) =>
                  onUpdate(index, { ...datum, matCon: value })
                }
                error={errors?.matCon}
                touched={touched?.matCon}
                placeholder="Select material condition"
              />
            </Box>
          )}

          <IconButton
            aria-label="Remove datum"
            size="sm"
            onClick={() => onRemove(index)}
          >
            <CloseIcon />
          </IconButton>
        </HStack>
      </HStack>
    </FormControl>
  );
};

// Reusable ControlFrameFields component for use in CompositeControlFrameForm
type ControlFrameFieldsProps = {
  values: ControlFrameFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  errors: unknown;
  touched: unknown;
  fieldPrefix?: string; // For nested forms like "controlFrames[0]."
  includeCharacteristic?: boolean;
};

export const ControlFrameFields = ({
  values,
  setFieldValue,
  errors,
  touched,
  fieldPrefix = '',
  includeCharacteristic = true,
}: ControlFrameFieldsProps) => {
  const getFieldName = useCallback(
    (field: string) => (fieldPrefix ? `${fieldPrefix}${field}` : field),
    [fieldPrefix],
  );

  const getFieldError = useCallback(
    (field: string) => getFieldErrorUtils(getFieldName(field), errors),
    [errors, getFieldName],
  );

  const getFieldTouched = useCallback(
    (field: string) => getFieldTouchedUtils(getFieldName(field), touched),
    [touched, getFieldName],
  );

  return (
    <>
      {/* Characteristic Select */}
      {includeCharacteristic && (
        <CharacteristicSelect
          name={getFieldName('characteristic')}
          label="Characteristic"
          value={values.characteristic}
          onChange={(value) =>
            setFieldValue(getFieldName('characteristic'), value)
          }
          error={getFieldError('characteristic')}
          touched={getFieldTouched('characteristic')}
        />
      )}

      {/* Dimension Type Select */}
      <DimensionTypeSelect
        name={getFieldName('dimType')}
        label="Dimension Type"
        value={values.dimType}
        onChange={(value) => setFieldValue(getFieldName('dimType'), value)}
        error={getFieldError('dimType')}
        touched={getFieldTouched('dimType')}
      />

      {/* Value Input */}
      <FormControl>
        <FormLabel>Tolerance Value</FormLabel>
        <NumberValueField
          name={getFieldName('value')}
          value={values.value}
          onChange={(value) => setFieldValue(getFieldName('value'), value)}
          size="sm"
          min={0}
        />
        {getFieldError('value') && getFieldTouched('value') && (
          <FormErrorMessage>{getFieldError('value')}</FormErrorMessage>
        )}
      </FormControl>

      {/* Material Condition Checkbox and Select */}
      <Checkbox
        name={getFieldName('hasMatCon')}
        isChecked={values.hasMatCon}
        onChange={(e) => {
          setFieldValue(getFieldName('hasMatCon'), e.target.checked);
          if (e.target.checked) {
            setFieldValue(getFieldName('matCon'), MATERIAL_CONDITIONS.MMC);
          } else {
            setFieldValue(getFieldName('matCon'), undefined);
          }
        }}
        size="sm"
      >
        Include material condition
      </Checkbox>
      {values.hasMatCon && (
        <MaterialConditionSelect
          name={getFieldName('matCon')}
          label="Material Condition"
          value={values.matCon}
          onChange={(value) => setFieldValue(getFieldName('matCon'), value)}
          error={getFieldError('matCon')}
          touched={getFieldTouched('matCon')}
        />
      )}

      {/* Datum References */}
      <FormControl>
        <FormLabel>Datum References</FormLabel>
        <VStack spacing={1}>
          {values.datums.map((datum, index) => (
            <DatumField
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              datum={datum}
              index={index}
              onUpdate={(idx, updatedDatum) => {
                const newDatums = [...values.datums];
                newDatums[idx] = updatedDatum;
                setFieldValue(getFieldName('datums'), newDatums);
              }}
              onRemove={(idx) => {
                const newDatums = values.datums.filter((_, i) => i !== idx);
                setFieldValue(getFieldName('datums'), newDatums);
              }}
              errors={getFieldError('datums')?.[index]}
              touched={getFieldTouched('datums')?.[index]}
            />
          ))}
          <ButtonGroup justifyContent="flex-end" width="100%">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // Get the next letter in the alphabet for the new datum
                const getNextDatumLetter = () => {
                  if (values.datums.length === 0) return 'A';

                  const lastDatum = values.datums[values.datums.length - 1];
                  if (!lastDatum.datum || lastDatum.datum.trim() === '')
                    return 'A';

                  const lastCharCode = lastDatum.datum.charCodeAt(0);
                  const nextCharCode = lastCharCode + 1;

                  // If we've reached Z, wrap back to A
                  if (nextCharCode > 90) return 'A';

                  return String.fromCharCode(nextCharCode);
                };

                const nextDatumValue = {
                  ...initialDatumValue,
                  datum: getNextDatumLetter(),
                };
                setFieldValue(getFieldName('datums'), [
                  ...values.datums,
                  { ...nextDatumValue },
                ]);
              }}
            >
              Add Datum Reference
            </Button>
          </ButtonGroup>
          {/* Array-level validation error */}
          {getFieldError('datums') &&
            getFieldTouched('datums') &&
            typeof getFieldError('datums') === 'string' && (
              <FormErrorMessage>{getFieldError('datums')}</FormErrorMessage>
            )}
        </VStack>
      </FormControl>
    </>
  );
};

const MAT_CON_SYMBOLS = {
  [MATERIAL_CONDITIONS.MMC]: 'Ⓜ',
  [MATERIAL_CONDITIONS.LMC]: 'Ⓛ',
};

// Mapping from API MaterialCondition to GDT modifiers
const MATERIAL_CONDITION_TO_GDT_MODIFIER: Record<
  MaterialCondition,
  GDTModifier
> = {
  [MATERIAL_CONDITIONS.MMC]: 'Ⓜ',
  [MATERIAL_CONDITIONS.LMC]: 'Ⓛ',
};

// Mapping from API DimensionType to GDT ToleranceZoneShape
const DIMENSION_TYPE_TO_TOLERANCE_ZONE_SHAPE: Partial<
  Record<DimensionType, ToleranceZoneShape>
> = {
  [DIMENSION_TYPES.DIAMETER]: 'diameter',
  // Linear and Angular don't have specific tolerance zone shapes
};

/**
 * Converts ControlFrameFormValues to GDTControlFrameProps for preview
 * @param values - The control frame form values to convert
 * @returns Props for the GDTControlFrame component
 */
export const convertToGDTControlFrameProps = (
  values: ControlFrameFormValues,
): GDTControlFrameProps => {
  // Convert characteristic (they should be the same symbols)
  const characteristic = values.characteristic as GDTCharacteristic;

  // Convert tolerance value to string
  const tolerance = values.value.toString();

  // Determine unit based on dimension type
  const unit = values.dimType === DIMENSION_TYPES.ANGULAR ? '°' : undefined;

  // Build modifiers array
  const modifiers: GDTModifier[] = [];
  if (values.hasMatCon && values.matCon) {
    modifiers.push(MATERIAL_CONDITION_TO_GDT_MODIFIER[values.matCon]);
  }

  // Determine tolerance zone shape from dimension type
  const toleranceZoneShape =
    DIMENSION_TYPE_TO_TOLERANCE_ZONE_SHAPE[values.dimType] || 'none';

  // Convert datum references
  const datumReferences: GDTDatumReference[] = values.datums
    .filter((datum) => datum.datum && datum.datum.trim() !== '')
    .map((datum) => ({
      datum: datum.datum,
      modifiers:
        datum.hasMatCon && datum.matCon
          ? [MATERIAL_CONDITION_TO_GDT_MODIFIER[datum.matCon]]
          : undefined,
    }));

  return {
    characteristic,
    tolerance,
    unit,
    modifiers,
    toleranceZoneShape,
    datumReferences,
    size: 'sm', // Use small size for preview
  };
};

/**
 * Converts ControlFrameFormValues into a text summary for display
 * @param values - The control frame form values to convert
 * @returns A formatted string representation of the control frame
 */
export const formatControlFrameSummary = (
  values: ControlFrameFormValues,
): string => {
  const parts: string[] = [];

  // Add characteristic symbol
  parts.push(values.characteristic);

  // Add tolerance value with dimension type indicator
  let toleranceSection = '';

  // Add diameter symbol if it's a diameter dimension
  if (values.dimType === DIMENSION_TYPES.DIAMETER) {
    toleranceSection += '⌀';
  }

  toleranceSection += values.value;

  // Add unit for angular dimensions
  if (values.dimType === DIMENSION_TYPES.ANGULAR) {
    toleranceSection += '°';
  }

  // Add material condition if present
  if (values.hasMatCon && values.matCon) {
    toleranceSection += ` ${MAT_CON_SYMBOLS[values.matCon]}`;
  }

  parts.push(toleranceSection);

  // Add datum references
  values.datums.forEach((datum) => {
    if (datum.datum && datum.datum.trim() !== '') {
      let datumSection = datum.datum;
      if (datum.hasMatCon && datum.matCon) {
        datumSection += ` ${MAT_CON_SYMBOLS[datum.matCon]}`;
      }
      parts.push(datumSection);
    }
  });

  return parts.join(' | ');
};

// Utility function to convert form values to FeatureControlFrame
export const convertToFeatureControlFrame = (
  values: ControlFrameFormValues,
): FeatureControlFrame => {
  const valueObject = buildPrecisionValueObject(values.value);
  if (!valueObject) {
    throw new Error('Invalid value provided to control frame');
  }

  const datums: DatumReference[] = values.datums.map((datum) => ({
    datum: datum.datum,
    matCon: datum.hasMatCon ? datum.matCon : undefined,
  }));

  return {
    calloutType: RUNES_CALLOUT_TYPES.CONTROL_FRAME,
    characteristic: values.characteristic,
    dimType: values.dimType,
    value: valueObject,
    matCon: values.hasMatCon ? values.matCon : undefined,
    datums,
  };
};

export const ControlFrameForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: ControlFrameFormValues) => {
      const controlFrame = convertToFeatureControlFrame(values);
      onSubmit(controlFrame, formatControlFrameSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialControlFrameValues}
      validationSchema={controlFrameValidationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Control Frame"
        >
          {/* Preview Section */}
          <FormPreviewSection>
            <GDTControlFrame {...convertToGDTControlFrameProps(values)} />
          </FormPreviewSection>

          {/* Form Fields */}
          <Flex direction="column" gap={1} px={1} pb={1}>
            <ControlFrameFields
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
            />
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
